import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';
import * as StockManagerActions from '../store/stock-manager.actions';
import {
  addOriginalValueFieldToItems,
  checkForDisabledItems,
  clearClientFilteredStockItemsCacheForStore,
  restoreStockManagerStateAfterPreview,
  setClientFilteredStockItemsCacheForStore,
} from './stock-manager.reducer.utils';
import {IPaginationData} from '../../../shared/shared-models/pagination/pagination-data';
import {ISearchableFields} from '../../../shared/shared-models/type-sense/default-searchable-fields';
import {IStockManagerPreviewSnapshot} from '../models/stock-manager-preview-snapshot.interface';
import {IFacetCounts, ITypeSenseApiDoc} from '../../../shared/shared-models/type-sense/type-sense-types';
import {IDepartment} from '../../../shared/shared-models/stock/departments';
import {StoreHeaderMenuData} from '../../../shared-utilities/models-old/ngp-report-grid/header-menu-data';
import * as NGPReportUtils from '../../ngp-report/store/ngp-report.reducer.utils';
import {PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS} from '../../../shared/shared-utils/price/price-banding.utils';
import {ColDef} from 'ag-grid-community';
import {getDeepCopyOfObject} from '../../../shared/shared-utils/object/object.utils';
import {ISharedStoreSettings} from '../../../shared/shared-models/firebase/shared-store-settings';
import {IStore} from '../../../shared/shared-models/store/store';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IDisableStockItemRule, IEnableStockItemRule, TLineColour} from '../../settings-new/models';
import {
  ensureDepartmentNameFields,
  toGridVisibleField,
} from '../../../shared/shared-utils/stock/department-display.utils';
import {stockManagerHasEditableFieldChanges} from '../utils/stock-manager-update.utils';
import {applyLineColoursToStockManager} from '../utils/stock-manager-line-colour.utils';
import {IBulkChangeExcludedItem} from '../models/stock-manager-bulk-change.model';
import {INITIAL_STOCK_DISABLE_AUDIT, IStockDisableAuditState} from '../models/stock-disable-audit.interface';
import {editableFields} from '../models/stock-manager-editable-fields';
import {ICreateNewStockItemSubmitState} from '../models/add-stock-item-interfaces';
import {IStockManagerUpdateBatchResult} from '../models/stock-manager-store-update-summary.interface';
import * as SharedFilterActions from '../../core/store-shared-filter/shared-filter.actions';
import {MULTI_STORE_STATE_KEY} from '../utils/stock-manager-multi-store.utils';

export interface StockManagerState {
  apiDocs: Record<string, ITypeSenseApiDoc>;
  autoFillSupplierStockCodeEnabled: boolean;
  bulkChangeExcludedItems: IBulkChangeExcludedItem[] | null;
  bulkChangeStatusMessage: string;
  clientFilteredStockItemsCache: Record<string, StockItem[]>;
  createNewStockItemSubmit: ICreateNewStockItemSubmitState;
  departments: Record<string, IDepartment[]>;
  disableAudit: IStockDisableAuditState;
  disableAuditNotice: string | null;
  disableAuditVisibleCodes: string[] | null;
  disabledRules: Record<string, IDisableStockItemRule>;
  dontAskSupplierCodeAutoFillSession: boolean;
  dontDisplayModalAgain: boolean;
  editedItems: Record<string, Record<string, StockItem>>;
  editedItemsFromTypesense: StockItem[];
  enabledRules: Record<string, IEnableStockItemRule>;
  errors: IGlobalError[],
  facetCounts: Record<string, IFacetCounts>;
  filters: Record<string, Record<string, ISearchableFields>>;
  haveStockItemsUpdated: boolean;
  headerMenuData: StoreHeaderMenuData;
  isBulkChangePreparing: boolean;
  isBulkChangeSubmitting: boolean;
  isDepartmentsLoading: boolean;
  isPaginationLoading: boolean;
  isPriceBanding: boolean;
  isStockItemClientFilterLoading: boolean;
  isStockItemFilterLoading: boolean;
  isStockItemsLoading: boolean;
  isStockItemsUpdating: boolean;
  isStockManagerLoading: boolean;
  isSuppliersLoading: boolean;
  lineColours: Record<string, TLineColour>;
  multiStoreBeyondPrefixCap: boolean;
  multiStoreColourMap: Record<string, string>;
  multiStoreSelectedStoreIds: string[];
  multiplePriceChangeNotice: {
    acknowledgedStoreIds: string[];
    show: boolean;
  };
  multiplePriceChangePreviewCode: string | null;
  nextStockItemsPage: Record<string, StockItem[]>;
  paginationData: Record<string, IPaginationData>;
  previewColumnData: Record<number, boolean>;
  previewSnapshotBeforePreview: Record<string, IStockManagerPreviewSnapshot>;
  searchableFields: ISearchableFields;
  stockItems: Record<string, StockItem[]>;
  stockUpdateBatchResults: IStockManagerUpdateBatchResult[];
  stockUpdatesInFlight: number;
  storeSettings: Record<string, ISharedStoreSettings>;
  storeSuppliers: Record<string, ISearchableFields>;
  visibleFields: Record<string, boolean>;
}

/**
 * Keeps an open audit result on screen when a stock-page request that started earlier finishes.
 */
const keepStockDisableAuditGrid = (state: StockManagerState): StockManagerState => ({
  ...state,
  isPaginationLoading: false,
  isStockItemClientFilterLoading: false,
  isStockItemFilterLoading: false,
  isStockItemsLoading: false,
  isStockManagerLoading: false,
});

/**
 * Removes editable fields the item has picked up since its original value snapshot was taken. Fields
 * that were absent from the source document have no original value to revert to, so leaving them in
 * place keeps the cell highlighted and the row dirty even though the item is no longer marked edited.
 */
const clearEditableFieldsMissingFromOriginalValue = (item: StockItem): void => {
  editableFields.forEach((field: string) => {
    if (item.originalValue?.[field] === undefined && field in item) {
      delete item[field];
    }
  });
};

export const initialStockUpdatesState: StockManagerState = {
  departments: {},
  disableAudit: INITIAL_STOCK_DISABLE_AUDIT,
  disableAuditNotice: null,
  disableAuditVisibleCodes: null,
  storeSettings: {},
  errors: [],
  facetCounts: {},
  filters: {},
  apiDocs: {},
  clientFilteredStockItemsCache: {},
  bulkChangeExcludedItems: null,
  bulkChangeStatusMessage: '',
  isBulkChangePreparing: false,
  isBulkChangeSubmitting: false,
  isDepartmentsLoading: false,
  isStockItemFilterLoading: false,
  isStockItemClientFilterLoading: false,
  isStockItemsLoading: false,
  isStockItemsUpdating: false,
  isStockManagerLoading: false,
  isPaginationLoading: false,
  isSuppliersLoading: false,
  paginationData: {},
  stockItems: {},
  nextStockItemsPage: {},
  editedItems: {},
  searchableFields: {},
  storeSuppliers: {},
  disabledRules: {},
  enabledRules: {},
  headerMenuData: {},
  lineColours: {},
  multiplePriceChangeNotice: {acknowledgedStoreIds: [], show: false},
  multiplePriceChangePreviewCode: null,
  visibleFields: {},
  isPriceBanding: false,
  editedItemsFromTypesense: [],
  previewSnapshotBeforePreview: {},
  previewColumnData: {},
  dontDisplayModalAgain: false,
  autoFillSupplierStockCodeEnabled: true,
  createNewStockItemSubmit: {status: 'idle'},
  dontAskSupplierCodeAutoFillSession: false,
  haveStockItemsUpdated: false,
  multiStoreSelectedStoreIds: [],
  multiStoreColourMap: {},
  multiStoreBeyondPrefixCap: false,
  stockUpdateBatchResults: [],
  stockUpdatesInFlight: 0,
};

const createStockManagerReducer: ActionReducer<StockManagerState> = createReducer(
  initialStockUpdatesState,
  // =======================================================================
  // Stock Item
  // =======================================================================
  on(StockManagerActions.getAllTypesenseApiKeysForStoresSuccess, (state: StockManagerState, {apiDocs}) => ({
    ...state,
    apiDocs,
  })),
  on(StockManagerActions.getAllTypesenseApiKeysForStoresFailure, (state: StockManagerState, {error}) => ({
    ...state,
    errors: [
      ...state.errors,
      error,
    ],
  })),
  on(StockManagerActions.getLineColours, (state: StockManagerState) => ({
    ...state,
    isStockItemsLoading: true,
  })),
  on(StockManagerActions.getAllStockItems, (state: StockManagerState) => ({
    ...state,
    disableAuditNotice: null,
    disableAuditVisibleCodes: null,
    isStockManagerLoading: true,
    isStockItemsLoading: true,
  })),
  on(StockManagerActions.getAllStockItemsSuccess, (state: StockManagerState, {
    stockItems,
    store,
    paginationData,
    facetCounts,
  }) => {
    if (state.disableAuditNotice) {
      return keepStockDisableAuditGrid(state);
    }
    if (state?.storeSettings?.[store.storeId]?.lineColoursEnabled) {
      stockItems = applyLineColoursToStockManager(
        state.lineColours[store.storeId],
        stockItems,
      );
    }
    stockItems = addOriginalValueFieldToItems<StockItem>(stockItems);
    stockItems = checkForDisabledItems(stockItems, state.disabledRules, true);

    return {
      ...state,
      stockItems: {
        ...state.stockItems,
        [store.storeId]: stockItems,
      },
      clientFilteredStockItemsCache: clearClientFilteredStockItemsCacheForStore(
        state.clientFilteredStockItemsCache,
        store.storeId,
      ),
      paginationData: {
        ...state.paginationData,
        [store.storeId]: paginationData,
      },
      facetCounts: {
        ...state.facetCounts,
        [store.storeId]: facetCounts,
      },
      isStockManagerLoading: false,
      isStockItemsLoading: false,
    };
  }),
  on(StockManagerActions.getAllStockItemsFailure, (state: StockManagerState, {error, store}) => ({
    ...state,
    stockItems: {
      ...state.stockItems,
      [store.storeId]: [],
    },
    paginationData: {
      ...state.paginationData,
      [store.storeId]: {
        currentPage: 1,
        pageSize: 20,
        totalItems: 0,
      },
    },
    facetCounts: {
      ...state.facetCounts,
      [store.storeId]: {},
    },
    isStockManagerLoading: false,
    isStockItemsLoading: false,
    errors: [...state.errors, error],
  })),
  // =======================================================================
  // Search Results
  // =======================================================================
  on(StockManagerActions.setSearchableFields, (state: StockManagerState, {searchableFields}) => ({
    ...state,
    searchableFields,
  })),
  on(StockManagerActions.getSearchResultsForStockItems, (state: StockManagerState) => ({
    ...state,
    disableAuditNotice: null,
    disableAuditVisibleCodes: null,
    isStockItemsLoading: true,
  })),
  on(StockManagerActions.getSearchResultsForStockItemsSuccess, (state: StockManagerState, {
    stockItems,
    store,
    paginationData,
    facetCounts,
    stores,
  }) => {
    if (state.disableAuditNotice) {
      return keepStockDisableAuditGrid(state);
    }
    if (state?.storeSettings?.[store.storeId]?.lineColoursEnabled) {
      stockItems = applyLineColoursToStockManager(
        state.lineColours[store.storeId],
        stockItems,
      );
    }
    stockItems = checkForDisabledItems(stockItems, state.disabledRules);
    stockItems = addOriginalValueFieldToItems<StockItem>(stockItems);
    const updatedSearchValueState = stores.reduce((acc, store: IStore) => {
      acc[store.storeId] = {
        ...state.paginationData[store.storeId],
        searchedValue: paginationData.searchedValue,
      };
      return acc;
    }, {} as Record<string, typeof paginationData>);

    return {
      ...state,
      stockItems: {
        ...state.stockItems,
        [store.storeId]: stockItems,
      },
      clientFilteredStockItemsCache: clearClientFilteredStockItemsCacheForStore(
        state.clientFilteredStockItemsCache,
        store.storeId,
      ),
      paginationData: {
        ...updatedSearchValueState,
        [store.storeId]: paginationData,
      },
      facetCounts: {
        ...state.facetCounts,
        [store.storeId]: facetCounts,
      },
      isStockManagerLoading: false,
      isStockItemsLoading: false,
    };
  }),
  on(StockManagerActions.getSearchResultsForStockItemsFailure, (state: StockManagerState, {error, store}) => ({
    ...state,
    stockItems: {
      ...state.stockItems,
      [store.storeId]: [],
    },
    isStockItemsLoading: false,
    isStockManagerLoading: false,
    errors: [...state.errors, error],
  })),
  // =======================================================================
  // Pagination
  // =======================================================================
  on(StockManagerActions.getPaginationResultsForStockItemsSuccess, (state: StockManagerState, {
    stockItems,
    store,
    paginationData,
    facetCounts,
  }) => {
    if (state.disableAuditNotice) {
      return keepStockDisableAuditGrid(state);
    }
    if (state?.storeSettings?.[store.storeId]?.lineColoursEnabled) {
      stockItems = applyLineColoursToStockManager(
        state.lineColours[store.storeId],
        stockItems,
      );
    }
    stockItems = checkForDisabledItems(stockItems, state.disabledRules);
    stockItems = addOriginalValueFieldToItems<StockItem>(stockItems);
    return {
      ...state,
      stockItems: {
        ...state.stockItems,
        [store.storeId]: stockItems,
      },
      paginationData: {
        ...state.paginationData,
        [store.storeId]: paginationData,
      },
      facetCounts: {
        ...state.facetCounts,
        [store.storeId]: facetCounts,
      },
      isStockManagerLoading: false,
      isPaginationLoading: false,
    };
  }),
  on(StockManagerActions.getPaginationResultsForStockItemsFailure, (state: StockManagerState, {error, store}) => ({
    ...state,
    isStockManagerLoading: false,
    isPaginationLoading: false,
    stockItems: {
      ...state.stockItems,
      [store.storeId]: [],
    },
    errors: [...state.errors, error],
  })),
  on(StockManagerActions.setPageSizeForSharedGridAtStoreId, (state: StockManagerState, {pageSize, store}) => {
    const currentPage = state.paginationData[store.storeId].currentPage > (state.paginationData[store.storeId].totalItems / pageSize) ?
      Math.floor((state.paginationData[store.storeId].totalItems / pageSize) + 1) : state.paginationData[store.storeId].currentPage;
    return {
      ...state,
      paginationData: {
        ...state.paginationData,
        [store.storeId]: {
          ...state.paginationData[store.storeId],
          pageSize,
          currentPage,
        },
      },
    };
  }),
  on(StockManagerActions.setStockManagerPaginationCurrentPage, (state: StockManagerState, {pageNumber, store}) => ({
    ...state,
    paginationData: {
      ...state.paginationData,
      [store.storeId]: {
        ...state.paginationData[store.storeId],
        currentPage: pageNumber,
      },
    },
    isStockManagerLoading: false,
    isPaginationLoading: false,
  })),
  on(StockManagerActions.setNextPageForSharedGridAtStoreId, (state: StockManagerState, {paginationData, store}) => {
    if (state.disableAuditNotice) {
      return state;
    }
    return {
      ...state,
      paginationData: {
        ...state.paginationData,
        [store.storeId]: paginationData,
      },
      stockItems: {
        ...state.stockItems,
        [store.storeId]: [...state.nextStockItemsPage[store.storeId]],
      },
    };
  }),
  on(StockManagerActions.getNextPageForSharedGridSuccess, (state: StockManagerState, {store, stockItems}) => {
    stockItems = checkForDisabledItems(stockItems, state.disabledRules[store.storeId]);
    stockItems = addOriginalValueFieldToItems<StockItem>(stockItems);
    return {
      ...state,
      nextStockItemsPage: {
        ...state.nextStockItemsPage,
        [store.storeId]: stockItems,
      },
    };
  }),

  on(StockManagerActions.getNextPageForStockItemsSuccess, (state: StockManagerState, {
      stockItems,
      store,
      nextPaginationData,
    }) => {
      if (state.disableAuditNotice) {
        return state;
      }
      stockItems = checkForDisabledItems(stockItems, state.disabledRules[store.storeId]);
      stockItems = addOriginalValueFieldToItems<StockItem>(stockItems);

      return {
        ...state,
        nextStockItemsPage: {
          ...state.nextStockItemsPage,
          [store.storeId]: stockItems,
        },
        paginationData: {
          ...state.paginationData,
          [store.storeId]: nextPaginationData,
        },
      };
    },
  ),
  // =======================================================================
  // Suppliers
  // =======================================================================
  on(StockManagerActions.getPaginationResultsForStockItems, (state: StockManagerState) => ({
    ...state,
    isStockManagerLoading: true,
    isSuppliersLoading: true,
  })),
  on(StockManagerActions.getSuppliersForStockManagerSuccess, (state: StockManagerState, {suppliers, store}) => ({
    ...state,
    storeSuppliers: {
      ...state.storeSuppliers,
      [store.storeId]: suppliers,
    },
    isStockManagerLoading: false,
    isSuppliersLoading: false,
  })),
  on(StockManagerActions.getSuppliersForStockManagerFailure, (state: StockManagerState, {error, store}) => ({
    ...state,
    storeSuppliers: {
      ...state.storeSuppliers,
      [store.storeId]: {},
    },
    errors: [...state.errors, error],
    isStockManagerLoading: false,
    isSuppliersLoading: false,
  })),
  on(StockManagerActions.getStoreSuppliersSuccess, (state: StockManagerState, {store, suppliers}) => ({
    ...state,
    storeSuppliers: {
      ...state.storeSuppliers,
      [store.storeId]: suppliers,
    },
  })),
  on(StockManagerActions.getStoreSuppliersFailure, (state: StockManagerState, {store, error}) => ({
    ...state,
    storeSuppliers: {
      ...state.storeSuppliers,
      [store.storeId]: {},
    },
    errors: [...state.errors, error],
  })),
  on(StockManagerActions.setSuppliersForStockManager, (state: StockManagerState) => ({
    ...state,
    isStockManagerLoading: true,
    isSuppliersLoading: true,
  })),
  on(StockManagerActions.setSuppliersForStockManagerSuccess, (state: StockManagerState, {
    stockItems,
    store,
    paginationData,
    facetCounts,
    suppliers,
  }) => ({
    ...state,
    stockItems: {
      ...state.stockItems,
      [store.storeId]: stockItems,
    },
    paginationData: {
      ...state.paginationData,
      [store.storeId]: paginationData,
    },
    facetCounts: {
      ...state.facetCounts,
      [store.storeId]: facetCounts,
    },
    storeSuppliers: {
      ...state.storeSuppliers,
      [store.storeId]: suppliers['40'],
    },
    isStockManagerLoading: false,
    isSuppliersLoading: false,
  })),
  on(StockManagerActions.setSuppliersForStockManagerFailure, (state: StockManagerState, {error, store}) => ({
    ...state,
    storeSuppliers: {
      ...state.storeSuppliers,
      [store.storeId]: {},
    },
    errors: [...state.errors, error],
    isStockManagerLoading: false,
    isSuppliersLoading: false,
  })),
  // =======================================================================
  // Departments and SubDepartments
  // =======================================================================
  on(StockManagerActions.getStoreDepartmentsForStockManager, (state: StockManagerState) => ({
    ...state,
    isDepartmentsLoading: true,
    isStockManagerLoading: true,
  })),
  on(StockManagerActions.getStoreDepartmentsForStockManagerSuccess, (state, {store, departments, storeSettings}) => {
    return {
      ...state,
      isDepartmentsLoading: false,
      isStockManagerLoading: true,
      departments: {
        ...state.departments,
        [store.storeId]: departments,
      },
      storeSettings: {
        ...state.storeSettings,
        [store.storeId]: storeSettings,
      },
    };
  }),
  on(StockManagerActions.getStoreDepartmentsForStockManagerFailure, (state, {error, store}) => ({
    ...state,
    departments: {
      ...state.departments,
      [store.storeId]: [],
    },
    isDepartmentsLoading: false,
    isStockManagerLoading: true,
    errors: [...state.errors, error],
  })),
  on(StockManagerActions.getStoreDepartmentsSuccess, (state: StockManagerState, {store, departments}) => ({
    ...state,
    departments: {
      ...state.departments,
      [store.storeId]: departments,
    },
  })),
  on(StockManagerActions.getStoreDepartmentsFailure, (state: StockManagerState, {store, error}) => ({
    ...state,
    departments: {
      ...state.departments,
      [store.storeId]: [],
    },
    errors: [...state.errors, error],
  })),
  // =======================================================================
  // Filters
  // =======================================================================
  on(StockManagerActions.setStockItemFilters, (state: StockManagerState) => ({
    ...state,
    disableAuditNotice: null,
    disableAuditVisibleCodes: null,
    isStockItemFilterLoading: true,
    isStockManagerLoading: true,
  })),
  on(SharedFilterActions.applyAdvancedFilterGroupFilterToGrid, (state: StockManagerState) => ({
    ...state,
    disableAuditNotice: null,
    disableAuditVisibleCodes: null,
    isStockItemFilterLoading: true,
    isStockManagerLoading: true,
  })),
  on(SharedFilterActions.removeAdvancedFilterGroupFilterFromGrid, (state: StockManagerState) => ({
    ...state,
    disableAuditNotice: null,
    disableAuditVisibleCodes: null,
    isStockItemFilterLoading: true,
    isStockManagerLoading: true,
  })),
  on(StockManagerActions.setStockItemClientFilterLoading, (state: StockManagerState, {loading}) => ({
    ...state,
    isStockItemClientFilterLoading: loading,
  })),
  on(StockManagerActions.clearClientFilteredStockItemsCache, (state: StockManagerState, {storeId}) => ({
    ...state,
    clientFilteredStockItemsCache: clearClientFilteredStockItemsCacheForStore(
      state.clientFilteredStockItemsCache,
      storeId,
    ),
  })),
  on(StockManagerActions.setStockItemFiltersSuccess, (state: StockManagerState, {
    stockItems,
    store,
    paginationData,
    facetCounts,
    filters,
    clientFilteredStockItemsCache,
  }) => {
    if (state?.storeSettings?.[store.storeId]?.lineColoursEnabled) {
      stockItems = applyLineColoursToStockManager(
        state.lineColours[store.storeId],
        stockItems,
      );
    }
    stockItems = checkForDisabledItems(stockItems, state.disabledRules);
    stockItems = addOriginalValueFieldToItems<StockItem>(stockItems);
    return {
      ...state,
      stockItems: {
        ...state.stockItems,
        [store.storeId]: stockItems,
      },
      clientFilteredStockItemsCache: setClientFilteredStockItemsCacheForStore(
        state.clientFilteredStockItemsCache,
        store.storeId,
        clientFilteredStockItemsCache,
      ),
      paginationData: {
        ...state.paginationData,
        [store.storeId]: paginationData,
      },
      facetCounts: {
        ...state.facetCounts,
        [store.storeId]: facetCounts,
      },
      filters: {
        ...state.filters,
        [store.storeId]: filters,
      },
      isStockItemFilterLoading: false,
      isStockItemClientFilterLoading: false,
      isStockManagerLoading: false,
    };
  }),
  on(StockManagerActions.setStockItemFiltersFailure, (state, {error, store}) => ({
    ...state,
    isStockItemFilterLoading: false,
    isStockItemClientFilterLoading: false,
    isStockManagerLoading: false,
    filters: {
      ...state.filters,
      [store.storeId]: {},
    },
    errors: [...state.errors, error],
  })),
  // =======================================================================
  // Item Disabling
  // =======================================================================
  on(StockManagerActions.getStockItemDisablingRulesSuccess, (state: StockManagerState, {store, rulesDoc, enableRules}) => ({
    ...state,
    disabledRules: {
      ...state.disabledRules,
      [store.storeId]: rulesDoc,
    },
    enabledRules: enableRules
      ? {
        ...state.enabledRules,
        [store.storeId]: enableRules,
      }
      : state.enabledRules,
  })),
  on(StockManagerActions.getStockItemDisablingRulesFailure, (state: StockManagerState, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  on(StockManagerActions.setItemDisabledWithStoreID, (state: StockManagerState, {stockItem, store}) => {
    const stockItemsForStore = state.stockItems[store.storeId] || [];

    const index = stockItemsForStore.findIndex((item: StockItem) => item.code === stockItem.code);
    const updatedStockItemsForStore = [...stockItemsForStore];
    if (index !== -1) {
      updatedStockItemsForStore[index] = {...stockItemsForStore[index], ...stockItem};
    }
    return {
      ...state,
      stockItems: {
        ...state.stockItems,
        [store.storeId]: updatedStockItemsForStore,
      },
      editedItems: {
        ...state.editedItems,
        [store.storeId]: {
          ...state.editedItems[store.storeId],
          [stockItem.code]: {
            ...stockItem,
          },
        },
      },
    };
  }),
  on(StockManagerActions.updateStockItemsWithEditedSuccess, (state: StockManagerState, {
    stockItems,
    store,
  }) => {
    const editedItemsForStore = state.editedItems[store.storeId] ?? {};
    const rows = state.disableAuditNotice ? (state.stockItems[store.storeId] ?? []) : stockItems;
    const mergedStockItems = rows.map((stockItem: StockItem) => {
      const editedItem = editedItemsForStore[stockItem.code];
      if (editedItem) {
        const mergedItem = {
          ...stockItem,
          ...editedItem,
        };
        return {
          ...mergedItem,
          isEdited: stockManagerHasEditableFieldChanges(mergedItem),
        };
      }
      return stockItem;
    });

    return {
      ...state,
      stockItems: {
        ...state.stockItems,
        [store.storeId]: mergedStockItems,
      },
    };
  }),

  // ====================================================================================================
  // Set Editing and Sorting for Column
  // ====================================================================================================
  on(StockManagerActions.setStockManagerMenuActionsWithStore, (state: StockManagerState, {store, gridHeaderMenu}) => {
    const headerMenuData: StoreHeaderMenuData = NGPReportUtils.setNGPReportStoreHeaderMenuActions(
      store,
      state.headerMenuData,
      PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS,
      gridHeaderMenu,
    );
    return {
      ...state,
      headerMenuData,
    };
  }),

  on(StockManagerActions.setEditedItemAdd, (state: StockManagerState, {stockItem, store}) => {
    const itemCode = String(stockItem.code ?? '');
    const stockItemsForStore = state.stockItems[store.storeId] || [];
    const itemIndex = stockItemsForStore.findIndex((item: StockItem) => String(item.code) === itemCode);
    const existingEditedItem = state.editedItems[store.storeId]?.[itemCode]
      ?? state.editedItems[store.storeId]?.[stockItem.code];
    const mergedStockItem = {
      ...(existingEditedItem ?? {}),
      ...stockItem,
      code: itemCode || stockItem.code,
      isEdited: true,
    };

    const updatedStockItemsForStore = [...stockItemsForStore];
    if (itemIndex !== -1) {
      updatedStockItemsForStore[itemIndex] = mergedStockItem;
    }

    return {
      ...state,
      stockItems: {
        ...state.stockItems,
        [store.storeId]: updatedStockItemsForStore,
      },
      editedItems: {
        ...state.editedItems,
        [store.storeId]: {
          ...state.editedItems[store.storeId],
          [itemCode]: mergedStockItem,
        },
      },
    };
  }),

  on(StockManagerActions.setBatchUpdateEditedItems, (state: StockManagerState, {batchUpdateEditedItems, store}) => {
    const itemsByCode = batchUpdateEditedItems.reduce<Record<string, StockItem>>(
      (acc, item) => {
        acc[item.code] = {...item, isEdited: true};
        return acc;
      },
      {},
    );
    return {
      ...state,
      haveStockItemsUpdated: false,
      editedItems: {
        ...state.editedItems,
        [store.storeId]: {
          ...state.editedItems[store.storeId],
          ...itemsByCode,
        },
      },
    };
  }),

  on(StockManagerActions.setEditedItemRemove, (state: StockManagerState, {stockItem, store}) => {
    const editedItemsForStore = state.editedItems[store.storeId] || {};
    if (stockItem.code in editedItemsForStore) {
      const stockItemsForStore = state.stockItems[store.storeId] || [];
      const itemIndex = stockItemsForStore.findIndex((item: StockItem) => item.code === stockItem.code);

      const updatedStockItemsForStore = [...stockItemsForStore];
      if (itemIndex !== -1 && stockItem.originalValue) {
        const revertedItem = {...stockItemsForStore[itemIndex]};
        Object.keys(stockItem.originalValue).forEach(key => {
          if (stockItem.originalValue[key] && stockItem.originalValue[key].value !== undefined) {
            revertedItem[key] = stockItem.originalValue[key].value;
          }
        });
        clearEditableFieldsMissingFromOriginalValue(revertedItem);
        revertedItem.isEdited = false;
        updatedStockItemsForStore[itemIndex] = revertedItem;
      }

      const {[stockItem.code]: removedItem, ...updatedEditedItemsForStore} = editedItemsForStore;
      const updatedEditedItems = Object.keys(updatedEditedItemsForStore).length > 0
        ? {
          ...state.editedItems,
          [store.storeId]: updatedEditedItemsForStore,
        }
        : Object.keys(state.editedItems).reduce((acc, key) => {
          if (key !== store.storeId) {
            acc[key] = state.editedItems[key];
          }
          return acc;
        }, {});

      return {
        ...state,
        stockItems: {
          ...state.stockItems,
          [store.storeId]: updatedStockItemsForStore,
        },
        editedItems: updatedEditedItems,
      };
    } else {
      return state;
    }
  }),

  // ====================================================================================================
  // Set VisibleFields
  // ====================================================================================================
  on(StockManagerActions.setStockManagerVisibility, (state: StockManagerState, {colDef, menuData}) => {
    const updatedVisibleFields = {...state.visibleFields};
    colDef?.forEach((item: ColDef) => {
      const fieldIndex = menuData?.findIndex((index: string) => index === item.field);
      if (
        item.field === 'icons' ||
        item.field === 'code' ||
        item.field === 'desc'
      ) {
        updatedVisibleFields[item.field] = true;
      } else {
        updatedVisibleFields[item.field] = fieldIndex >= 0;
      }
    });
    return {
      ...state,
      visibleFields: updatedVisibleFields,
    };
  }),
  on(StockManagerActions.updateStockManagerSingleVisibleField, (state: StockManagerState, {colId, value}) => ({
    ...state,
    visibleFields: {
      ...state.visibleFields,
      [colId]: value,
    },
  })),
  // ====================================================================================================
  // Set Line Colour
  // ====================================================================================================
  on(StockManagerActions.getLineColoursSuccess, (state: StockManagerState, {lineColours, selectedStore}) => {

    return {
      ...state,
      lineColours: {
        ...state.lineColours,
        [selectedStore.storeId]: lineColours,
      },
    };
  }),
  on(StockManagerActions.getLineColoursFailure, (state: StockManagerState, {error, store}) => ({
    ...state,
    lineColours: {
      ...state.lineColours,
      [store.storeId]: null,
    },
    isStockItemsLoading: false,
    errors: [...state.errors, error],
  })),
  on(StockManagerActions.getStoreLineColoursSuccess, (state: StockManagerState, {store, lineColours}) => ({
    ...state,
    lineColours: {
      ...state.lineColours,
      [store.storeId]: lineColours,
    },
  })),
  on(StockManagerActions.getStoreLineColoursFailure, (state: StockManagerState, {store, error}) => ({
    ...state,
    lineColours: {
      ...state.lineColours,
      [store.storeId]: null,
    },
    errors: [...state.errors, error],
  })),

  // ====================================================================================================
  // Update Price, Nom Gp And GD Diff By StoreID
  // ====================================================================================================
  on(StockManagerActions.setPriceGpForStockMangerWithStoreID, (state: StockManagerState, {
    stockItem,
    field,
    storeId,
  }) => {
    const updatedStockItemsForStore = (state.stockItems[storeId.storeId] || []).map((item: StockItem) =>
      item.code === stockItem.code ? stockItem : item,
    );

    return {
      ...state,
      stockItems: {
        ...state.stockItems,
        [storeId.storeId]: updatedStockItemsForStore,
      },
    };
  }),
  on(StockManagerActions.togglePriceBanding, (state: StockManagerState) => ({
    ...state,
    isPriceBanding: !state.isPriceBanding,
  })),
  // ====================================================================================================
  // Multiple Price Change Rules
  // ====================================================================================================
  on(StockManagerActions.showMultiplePriceChangeNotice, (state: StockManagerState, {store}) => ({
    ...state,
    multiplePriceChangeNotice: {
      acknowledgedStoreIds: state.multiplePriceChangeNotice.acknowledgedStoreIds.includes(store.storeId)
        ? state.multiplePriceChangeNotice.acknowledgedStoreIds
        : [...state.multiplePriceChangeNotice.acknowledgedStoreIds, store.storeId],
      show: true,
    },
  })),
  on(StockManagerActions.dismissMultiplePriceChangeNotice, (state: StockManagerState) => ({
    ...state,
    multiplePriceChangeNotice: {
      ...state.multiplePriceChangeNotice,
      show: false,
    },
  })),
  on(StockManagerActions.openMultiplePriceChangePreview, (state: StockManagerState, {code}) => ({
    ...state,
    multiplePriceChangePreviewCode: code,
  })),
  on(StockManagerActions.closeMultiplePriceChangePreview, (state: StockManagerState) => ({
    ...state,
    multiplePriceChangePreviewCode: null,
  })),
  on(StockManagerActions.toggleAutoFillSupplierStockCode, (state: StockManagerState) => ({
    ...state,
    autoFillSupplierStockCodeEnabled: !state.autoFillSupplierStockCodeEnabled,
  })),
  on(StockManagerActions.setDontAskSupplierCodeAutoFillSession, (state: StockManagerState) => ({
    ...state,
    dontAskSupplierCodeAutoFillSession: true,
  })),
  on(StockManagerActions.clearStockManagerMenuActions, (state: StockManagerState) => ({
    ...state,
    headerMenuData: {},
  })),
  on(StockManagerActions.getEditedItemsFromTypesense, (state, {store}) => {
    const pagination = state.paginationData[store.storeId];

    return {
      ...state,
      isStockManagerLoading: true,
      previewSnapshotBeforePreview: {
        ...state.previewSnapshotBeforePreview,
        [store.storeId]: {
          pageSize: pagination?.pageSize ?? 100,
          currentPage: pagination?.currentPage ?? 1,
          searchedValue: pagination?.searchedValue ?? '',
          filters: getDeepCopyOfObject(state.filters[store.storeId] ?? {}),
        },
      },
      stockItems: {
        ...state.stockItems,
        [store.storeId]: [],
      },
    };
  }),
  on(StockManagerActions.getEditedItemsFromTypesenseSuccess, (state, {
    editedItemsFromTypesense,
    store,
    priceBandedItems,
  }) => ({
    ...state,
    isStockManagerLoading: false,
    editedItemsFromTypesense,
    stockItems: {
      ...state.stockItems,
      [store.storeId]: priceBandedItems,
    },
    paginationData: {
      ...state.paginationData,
      [store.storeId]: {
        ...state.paginationData[store.storeId],
        pageSize: editedItemsFromTypesense.length,
        currentPage: 1,
      },
    },
  })),
  on(StockManagerActions.getEditedItemsFromTypesenseFailure, (state) => ({
    ...state,
    isStockManagerLoading: false,
    editedItemsFromTypesense: [],
  })),
  on(StockManagerActions.setStockUpdatesForStockManger, (state) => {
    const currentStoreIds = Object.keys(state.stockItems);
    const updatedStockItems = {...state.stockItems};

    currentStoreIds.forEach((storeId: string) => {
      if (state.stockItems[storeId]) {
        updatedStockItems[storeId] = state.stockItems[storeId].map((item: StockItem) => {
          if (item.isEdited) {
            return {...item, isEdited: false};
          }
          return item;
        });
      }
    });

    return {
      ...state,
      stockItems: updatedStockItems,
      isStockItemsUpdating: true,
      haveStockItemsUpdated: true,
    };
  }),
  on(StockManagerActions.prepareBulkStockChanges, (state) => ({
    ...state,
    bulkChangeExcludedItems: null,
    bulkChangeStatusMessage: 'Checking matching stock items for exclusions…',
    isBulkChangePreparing: true,
  })),
  on(StockManagerActions.prepareBulkStockChangesSuccess, (state, {excludedItems}) => ({
    ...state,
    bulkChangeExcludedItems: excludedItems,
    bulkChangeStatusMessage: '',
    isBulkChangePreparing: false,
  })),
  on(StockManagerActions.prepareBulkStockChangesFailure, (state, {error}) => ({
    ...state,
    bulkChangeExcludedItems: null,
    bulkChangeStatusMessage: '',
    isBulkChangePreparing: false,
    errors: [...state.errors, error],
  })),
  on(StockManagerActions.applyBulkStockChanges, (state) => ({
    ...state,
    bulkChangeExcludedItems: null,
    bulkChangeStatusMessage: 'Loading matching stock items…',
    isBulkChangeSubmitting: true,
  })),
  on(StockManagerActions.setBulkChangeStatus, (state, {message}) => ({
    ...state,
    bulkChangeStatusMessage: message,
  })),
  on(StockManagerActions.applyBulkStockChangesSuccess, (state) => ({
    ...state,
    bulkChangeStatusMessage: '',
    isBulkChangeSubmitting: false,
  })),
  on(StockManagerActions.applyBulkStockChangesFailure, (state, {error, cancelled}) => ({
    ...state,
    bulkChangeStatusMessage: '',
    isBulkChangeSubmitting: false,
    errors: cancelled ? state.errors : [...state.errors, error],
  })),
  on(StockManagerActions.stockUpdateFailure, (state, {error}) => ({
    ...state,
    isStockItemsUpdating: state.stockUpdatesInFlight > 0,
    haveStockItemsUpdated: false,
    errors: [...state.errors, error],
  })),
  on(StockManagerActions.undoAllEditsToStockManager, (state) => {
    const currentStoreIds = Object.keys(state.stockItems);
    const updatedStockItems = {...state.stockItems};

    currentStoreIds.forEach((storeId: string) => {
      if (state.stockItems[storeId]) {
        updatedStockItems[storeId] = state.stockItems[storeId].map((item: StockItem) => {
          if (item.originalValue && item.isEdited) {
            const revertedItem = {...item};
            Object.keys(item.originalValue).forEach((key: string) => {
              if (item.originalValue[key] && item.originalValue[key].value !== undefined) {
                revertedItem[key] = item.originalValue[key].value;
              }
            });
            clearEditableFieldsMissingFromOriginalValue(revertedItem);
            revertedItem.isEdited = false;
            return revertedItem;
          }
          return item;
        });
      }
    });

    return {
      ...state,
      stockItems: updatedStockItems,
      haveStockItemsUpdated: true,
    };
  }),
  on(StockManagerActions.removeEditedItemsAfterStockUpdate, (state, {store}) => {
    const {paginationData, filters, previewSnapshotBeforePreview} = restoreStockManagerStateAfterPreview(
      state.paginationData,
      state.filters,
      state.previewSnapshotBeforePreview,
      store.storeId,
    );

    return {
      ...state,
      editedItems: {
        ...state.editedItems,
        [store.storeId]: {},
      },
      isStockItemsUpdating: state.stockUpdatesInFlight > 0,
      haveStockItemsUpdated: false,
      editedItemsFromTypesense: [],
      paginationData,
      filters,
      previewSnapshotBeforePreview,
    };
  }),
  on(StockManagerActions.exitStockManagerPreview, (state, {store}) => {
    const {paginationData, filters, previewSnapshotBeforePreview} = restoreStockManagerStateAfterPreview(
      state.paginationData,
      state.filters,
      state.previewSnapshotBeforePreview,
      store.storeId,
    );

    return {
      ...state,
      editedItemsFromTypesense: [],
      paginationData,
      filters,
      previewSnapshotBeforePreview,
    };
  }),

//===============================================================================================================
// Set the Stock Manager Preview Column Data
//===============================================================================================================

  on(StockManagerActions.setStockManagerPreviewColumnsSuccess, (state, {columnData}) => {
    const newVisibleFields = {...state.visibleFields};
    const columnFields = ensureDepartmentNameFields(Object.values(columnData).map((field: string) => String(field)));
    Object.keys(newVisibleFields).forEach((field: string) => {
      newVisibleFields[field] = columnFields.includes(field);
    });
    columnFields.forEach((field: string) => {
      newVisibleFields[toGridVisibleField(field)] = true;
    });
    return {
      ...state,
      previewColumnData: newVisibleFields,
    };
  }),
  on(StockManagerActions.setStockManagerPreviewColumnsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  on(StockManagerActions.setDontDisplayModalAgain, (state) => ({
    ...state,
    dontDisplayModalAgain: true,
  })),
  on(StockManagerActions.setEditedItemsWithValueFromLocalStorage, (state, {store, editedItems}) => ({
    ...state,
    dontDisplayModalAgain: true,
    editedItems: editedItems,
  })),
  on(StockManagerActions.lockAllStockItems, (state) => ({
    ...state,
    haveStockItemsUpdated: true,
  })),
  on(StockManagerActions.downloadFilteredItemsAsCSV, (state) => ({
    ...state,
    haveStockItemsUpdated: true,
  })),
  on(StockManagerActions.downloadFilteredItemsAsCSVSuccess, (state) => ({
    ...state,
    haveStockItemsUpdated: false,
  })),
  on(StockManagerActions.lockAllStockItemsFailure, (state) => ({
    ...state,
    haveStockItemsUpdated: false,
  })),
  on(StockManagerActions.runStockDisableAudit, (state: StockManagerState, {storeId}) => ({
    ...state,
    disableAudit: {
      ...state.disableAudit,
      errorMessage: null,
      status: 'running',
      storeId,
    },
  })),
  on(StockManagerActions.runStockDisableAuditSuccess, (state: StockManagerState, result) => ({
    ...state,
    disableAudit: {
      errorMessage: null,
      lastRunAt: Date.now(),
      disabledRuleMismatches: result.disabledRuleMismatches,
      missingDisableTag: result.missingDisableTag,
      partialDisableDescriptions: result.partialDisableDescriptions,
      rulesConfigured: result.rulesConfigured,
      scannedCount: result.scannedCount,
      status: 'ready',
      storeId: state.disableAudit.storeId,
      truncated: result.truncated,
    },
  })),
  on(StockManagerActions.runStockDisableAuditFailure, (state: StockManagerState, {errorMessage}) => ({
    ...state,
    disableAudit: {
      ...INITIAL_STOCK_DISABLE_AUDIT,
      errorMessage,
      lastRunAt: state.disableAudit.lastRunAt,
      status: 'error',
      storeId: state.disableAudit.storeId,
    },
  })),
  on(StockManagerActions.showStockDisableAuditItems, (state: StockManagerState, {notice, stockItems, store}) => {
    const pageSize = state.paginationData[store.storeId]?.pageSize ?? 100;
    let items = stockItems.map((item: StockItem) => item.originalValue
      ? item
      : addOriginalValueFieldToItems<StockItem>([item])[0]);
    if (state?.storeSettings?.[store.storeId]?.lineColoursEnabled) {
      items = applyLineColoursToStockManager(state.lineColours[store.storeId], items);
    }
    items = items.map((item: StockItem) => ({
      ...item,
      disabled: Array.isArray(item._tags) && item._tags.includes('disabled'),
    }));
    return {
      ...state,
      disableAuditNotice: notice,
      disableAuditVisibleCodes: items.map((item: StockItem) => String(item.code)),
      stockItems: {
        ...state.stockItems,
        [store.storeId]: items,
      },
      clientFilteredStockItemsCache: setClientFilteredStockItemsCacheForStore(
        state.clientFilteredStockItemsCache,
        store.storeId,
        items,
      ),
      filters: {
        ...state.filters,
        [store.storeId]: {},
      },
      paginationData: {
        ...state.paginationData,
        [store.storeId]: {
          currentPage: 1,
          pageSize,
          searchedValue: '',
          totalItems: items.length,
        },
      },
      isStockItemClientFilterLoading: false,
      isStockItemFilterLoading: false,
      isStockManagerLoading: false,
      isStockItemsLoading: false,
    };
  }),
  on(StockManagerActions.createNewStockItem, (state: StockManagerState) => ({
    ...state,
    createNewStockItemSubmit: {status: 'pending'},
  })),
  on(StockManagerActions.createNewStockItemSuccess, (state: StockManagerState) => ({
    ...state,
    createNewStockItemSubmit: {status: 'success'},
  })),
  on(StockManagerActions.createNewStockItemFailure, (state: StockManagerState, {failed, succeededStoreIds}) => ({
    ...state,
    createNewStockItemSubmit: {status: 'failure', failed, succeededStoreIds},
  })),
  on(StockManagerActions.resetCreateNewStockItemSubmit, (state: StockManagerState) => ({
    ...state,
    createNewStockItemSubmit: {status: 'idle'},
  })),

  on(StockManagerActions.setMultiStoreSelectedStoreIds, (state: StockManagerState, {storeIds}) => ({
    ...state,
    multiStoreSelectedStoreIds: storeIds ?? [],
    multiStoreBeyondPrefixCap: false,
    isStockItemsLoading: true,
    haveStockItemsUpdated: false,
  })),

  on(StockManagerActions.getMultiStoreStockItems, (state: StockManagerState) => ({
    ...state,
    isStockItemsLoading: true,
    haveStockItemsUpdated: false,
    multiStoreBeyondPrefixCap: false,
  })),

  on(StockManagerActions.getMultiStoreStockItemsSuccess, (state: StockManagerState, {
    stockItems,
    paginationData,
    facetCounts,
    storeColourMap,
  }) => ({
    ...state,
    isStockItemsLoading: false,
    isStockItemFilterLoading: false,
    isStockItemClientFilterLoading: false,
    isStockManagerLoading: false,
    haveStockItemsUpdated: true,
    multiStoreBeyondPrefixCap: false,
    multiStoreColourMap: storeColourMap || {},
    stockItems: {
      ...state.stockItems,
      [MULTI_STORE_STATE_KEY]: stockItems,
    },
    paginationData: {
      ...state.paginationData,
      [MULTI_STORE_STATE_KEY]: paginationData,
    },
    facetCounts: {
      ...state.facetCounts,
      [MULTI_STORE_STATE_KEY]: facetCounts,
    },
  })),

  on(StockManagerActions.getMultiStoreStockItemsFailure, (state: StockManagerState, {error}) => ({
    ...state,
    isStockItemsLoading: false,
    isStockItemFilterLoading: false,
    isStockItemClientFilterLoading: false,
    isStockManagerLoading: false,
    haveStockItemsUpdated: true,
    errors: [...(state.errors || []), error],
  })),

  on(StockManagerActions.getMultiStoreStockItemsBeyondCap, (state: StockManagerState, {paginationData}) => ({
    ...state,
    isStockItemsLoading: false,
    isStockItemFilterLoading: false,
    isStockItemClientFilterLoading: false,
    isStockManagerLoading: false,
    haveStockItemsUpdated: true,
    multiStoreBeyondPrefixCap: true,
    stockItems: {
      ...state.stockItems,
      [MULTI_STORE_STATE_KEY]: [],
    },
    paginationData: {
      ...state.paginationData,
      [MULTI_STORE_STATE_KEY]: paginationData,
    },
  })),

  on(StockManagerActions.setMultiStoreStockItemFilters, (state: StockManagerState, {filters}) => ({
    ...state,
    isStockItemsLoading: true,
    haveStockItemsUpdated: false,
    filters: {
      ...state.filters,
      [MULTI_STORE_STATE_KEY]: filters,
    },
  })),

  on(StockManagerActions.setStockUpdatesForStore, (state: StockManagerState) => ({
    ...state,
    stockUpdatesInFlight: state.stockUpdatesInFlight + 1,
    stockUpdateBatchResults: state.stockUpdatesInFlight === 0 ? [] : state.stockUpdateBatchResults,
    isStockItemsUpdating: true,
    haveStockItemsUpdated: false,
  })),

  on(StockManagerActions.finishStockUpdateForStore, (state: StockManagerState, result) => {
    const stockUpdatesInFlight = Math.max(0, state.stockUpdatesInFlight - 1);
    return {
      ...state,
      stockUpdatesInFlight,
      stockUpdateBatchResults: [
        ...state.stockUpdateBatchResults,
        {
          failed: result.failed,
          itemCount: result.itemCount,
          message: result.message,
          storeName: result.storeName,
        },
      ],
      isStockItemsUpdating: stockUpdatesInFlight > 0,
    };
  }),
);

export const stockManagerReducer = (state: StockManagerState, action: Action): StockManagerState => createStockManagerReducer(state, action);
